import React, { useMemo, useState } from 'react';
import './AccountDetailsDialog.css';

export interface UnassignedAccount {
  acctrefno: number;
  loan_number: string;
  welcome_call: string;
  welcome_call_date: string;
  phone_numbers: string;
}

interface UnassignedAccountsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: UnassignedAccount[];
  loading?: boolean;
}

const UnassignedAccountsDialog: React.FC<UnassignedAccountsDialogProps> = ({
  isOpen,
  onClose,
  accounts,
  loading = false
}) => {
  const [sortBy, setSortBy] = useState<'loan_number' | 'welcome_call' | 'welcome_call_date'>('loan_number');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [showOnlyNoWelcome, setShowOnlyNoWelcome] = useState(false);

  const handleSort = (column: 'loan_number' | 'welcome_call' | 'welcome_call_date') => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (column: 'loan_number' | 'welcome_call' | 'welcome_call_date') => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'ASC' ? '↑' : '↓';
  };

  const filteredAndSorted = useMemo(() => {
    let result = Array.isArray(accounts) ? [...accounts] : [];

    if (showOnlyNoWelcome) {
      result = result.filter(a => (a.welcome_call || '').toLowerCase() === 'no');
    }

    result.sort((a, b) => {
      let aVal: string = '';
      let bVal: string = '';

      if (sortBy === 'loan_number') {
        aVal = a.loan_number || '';
        bVal = b.loan_number || '';
      } else if (sortBy === 'welcome_call') {
        aVal = a.welcome_call || '';
        bVal = b.welcome_call || '';
      } else if (sortBy === 'welcome_call_date') {
        aVal = a.welcome_call_date || '';
        bVal = b.welcome_call_date || '';
      }

      if (aVal < bVal) return sortOrder === 'ASC' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'ASC' ? 1 : -1;
      return 0;
    });

    return result;
  }, [accounts, showOnlyNoWelcome, sortBy, sortOrder]);

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Unassigned Accounts - Welcome Call Status</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading unassigned accounts...</p>
            </div>
          ) : (
            <>
              <div className="summary-stats" style={{ marginBottom: '16px' }}>
                <div className="stat-item">
                  <span className="stat-label">Total Unassigned:</span>
                  <span className="stat-value">{(accounts || []).length.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <label className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={showOnlyNoWelcome}
                      onChange={(e) => setShowOnlyNoWelcome(e.target.checked)}
                    />
                    Show only Welcome Call = No
                  </label>
                </div>
              </div>

              <div className="table-container">
                <table className="accounts-table">
                  <thead>
                    <tr>
                      <th
                        className="sortable"
                        onClick={() => handleSort('loan_number')}
                        title="Click to sort"
                      >
                        Loan Number {getSortIcon('loan_number')}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort('welcome_call')}
                        title="Click to sort"
                      >
                        Welcome Call Made {getSortIcon('welcome_call')}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort('welcome_call_date')}
                        title="Click to sort"
                      >
                        Welcome Call Date {getSortIcon('welcome_call_date')}
                      </th>
                      <th>Phone Numbers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(filteredAndSorted) || filteredAndSorted.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="no-data">
                          No unassigned accounts found
                        </td>
                      </tr>
                    ) : (
                      filteredAndSorted.map((account, index) => (
                        <tr key={account.acctrefno ?? index}>
                          <td>{account.loan_number}</td>
                          <td>{account.welcome_call}</td>
                          <td>{account.welcome_call_date || ''}</td>
                          <td>{account.phone_numbers || ''}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="dialog-footer">
          <button className="close-dialog-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnassignedAccountsDialog;


